import React, { useEffect, useRef } from 'react';

import { MtxButton, MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { Trans, useTranslation } from 'react-i18next';

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import FixedActionsBar from '@/components/FixedActionsBar/FixedActionsBar';
import { BREAKPOINTS } from '@/constants/breakpoints';
import useScreenSize from '@/hooks/useScreenSize';
import toTrackingSlug from '@/utils/functions/tracking/toTrackingSlug/toTrackingSlug';
import trackQps from '@/utils/functions/tracking/trackQps/trackQps';

import styles from './styles.module.css';

import type { BreadcrumbItem } from '@/services/types/Breadcrumb';

interface QpsUnavailableProps {
  productName: string;
  breadcrumbItems: BreadcrumbItem[];
  onRetry: () => void;
  onBackToProposal: () => void;
}

const QpsUnavailable: React.FC<QpsUnavailableProps> = ({ productName, breadcrumbItems, onRetry, onBackToProposal }) => {
  const { t } = useTranslation();
  const { windowWidth } = useScreenSize();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const isDisplayTrackedRef = useRef(false);

  // La bascule questionnaire → indisponible ne change pas de route : sans ce recentrage, rien n'est
  // annoncé et le focus reste sur le bouton démonté.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (isDisplayTrackedRef.current) return;
    isDisplayTrackedRef.current = true;
    trackQps('questionnaire_indisponible', { codeSupport: toTrackingSlug(productName) });
  }, []);

  return (
    <div className={styles.screen}>
      {/* Seul écran QPS sans flèche de retour sous le header : la maquette mobile n'en montre pas,
          le retour passe par « Retourner à la proposition ». */}
      {windowWidth > BREAKPOINTS.tablet && <Breadcrumb items={breadcrumbItems} alternative onBack={onBackToProposal} />}

      <div className={styles.column}>
        <div className={styles.status}>
          <span aria-hidden="true" className={styles.icon}>
            <MtxIcon name="warning-solide" size={64} />
          </span>
          <h1 className={styles.title} ref={titleRef} tabIndex={-1}>
            {t('qps.unavailable.title')}
          </h1>
        </div>

        <p className={styles.message}>
          <Trans
            i18nKey="qps.unavailable.message"
            values={{ product: productName }}
            components={{ b: <strong className={styles.productName} /> }}
          />
        </p>
      </div>

      <FixedActionsBar centered stackOnMobile>
        <MtxButton
          className={styles.actionButton}
          variant="tertiary"
          size="large"
          fullWidth
          onClick={() => {
            trackQps('retour_proposition', { codeSupport: toTrackingSlug(productName) });
            onBackToProposal();
          }}
        >
          <span>{t('qps.unavailable.backToProposal')}</span>
        </MtxButton>
        <MtxButton className={styles.actionButton} variant="primary" size="large" fullWidth onClick={onRetry}>
          <span>{t('qps.unavailable.retry')}</span>
        </MtxButton>
      </FixedActionsBar>
    </div>
  );
};

export default QpsUnavailable;
